/**
 * Overview — the 2 AM front door. WIRED to the real API.
 *
 * Data: GET /api/tonight (mode, storm, stats, active outage, escalations) polled live, and
 * GET /api/flows for the pinned posture services on the quick-activate shelf.
 *
 * Invariants preserved from the contract and INTEGRATION.md:
 *   - Storm is its OWN dimension (tonight.stormArmed + POST /api/storm/arm|disarm), NOT a flow.
 *     Posture flows and storm are never one radio group.
 *   - A card turns "Armed" only from the server's confirmed result — never optimistically.
 *   - The outage announcement is shown verbatim; its wording is edited on the Outage screen,
 *     never rewritten here to match an ETA. announcementContradictsEta surfaces the conflict.
 *   - Controls are gated by real permissions (policy.write to arm a flow, outage.write for
 *     storm); client gating supplements, never replaces, server enforcement.
 *   - The demo marker reflects the real session demo state, not a hard-coded label.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Icon,
  Modal,
  Notice,
  Panel,
  Stat,
} from "../components/ui";
import { AnnouncementPlayer } from "../components/AnnouncementPlayer";
import { api } from "../../lib/api.ts";
import type { FlowListItem, RunFlowResponse, TonightResponse } from "../../lib/api.ts";
import { qk, useMutate, usePermissions, useDemoMode, useResource } from "../../lib/useApi.ts";

// Flows are not in the shared qk vocabulary; the real console keys the list [flows] and a
// detail [flow, id]. Match that so a mutation here invalidates the same cache the Flows screen reads.
const FLOWS_KEY = ["flows"] as const;

type Hero = { title: string; tone: "green" | "amber" | "neutral"; state: string; blurb: string };

function heroForMode(mode: TonightResponse["mode"]): Hero {
  switch (mode) {
    case "after_hours":
      return {
        title: "After-hours answering",
        tone: "green",
        state: "Active",
        blurb: "Answer calls · check outages · take messages · page on-call",
      };
    case "office_hours_standby":
      return {
        title: "Daytime standby",
        tone: "amber",
        state: "Standby",
        blurb: "Office hours · answering only if the line rolls over",
      };
    case "demo":
      return {
        title: "Demo mode",
        tone: "neutral",
        state: "Simulated",
        blurb: "Every outbound action is simulated — nothing leaves the building",
      };
  }
}

export function Overview() {
  const tonight = useResource<TonightResponse>(qk.tonight, () => api.tonight(), {
    refetchIntervalMs: 30_000,
    staleTimeMs: 15_000,
  });
  const flows = useResource(FLOWS_KEY, () => api.flows.list(), { staleTimeMs: 60_000 });
  const { can } = usePermissions();
  const { demo } = useDemoMode();
  const mayArmFlow = can("policy.write");
  const mayStorm = can("outage.write");

  const [services, setServices] = useState(false);

  const armStorm = useMutate({
    mutationFn: () => api.armStorm({}),
    success: "Storm response armed",
    touchesTonight: true,
    invalidate: [FLOWS_KEY],
  });
  const disarmStorm = useMutate({
    mutationFn: () => api.disarmStorm(),
    success: "Storm response turned off",
    touchesTonight: true,
  });
  const runFlow = useMutate<RunFlowResponse, string>({
    mutationFn: (id) => api.flows.run(id, {}),
    success: (d) => `${d.item.name} activated${d.simulated ? " · simulated" : ""}`,
    touchesTonight: true,
    invalidate: [FLOWS_KEY],
  });
  const stormBusy = armStorm.isPending || disarmStorm.isPending;

  // -------------------------------------------------------------- loading / error
  if (tonight.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={28} />
        <p>Opening tonight…</p>
      </div>
    );
  }
  if (tonight.isError || !tonight.data) {
    return (
      <Panel title="Tonight is unavailable">
        <p className="muted">
          The console could not reach NightDesk{tonight.error?.detail ? `: ${tonight.error.detail}` : "."} The
          phone line does not depend on this page.
        </p>
        <Button variant="primary" onClick={() => void tonight.refetch()} loading={tonight.isFetching}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }

  const t = tonight.data;
  const hero = heroForMode(t.mode);
  const outage = t.activeOutage;
  const openEscalations = t.escalationsList.filter((e) => e.state !== "resolved");
  const notifying = openEscalations.some((e) => e.state === "notifying");

  const postureFlows: FlowListItem[] = (flows.data?.items ?? [])
    .filter((f) => f.pinned && f.kind === "posture")
    .sort((a, b) => a.pinOrder - b.pinOrder);

  function activateFlow(flow: FlowListItem) {
    if (flow.armed || !mayArmFlow) return;
    runFlow.mutate(flow.id, { onSuccess: () => setServices(false) });
  }
  function toggleStorm() {
    if (!mayStorm || stormBusy) return;
    (t.stormArmed ? disarmStorm : armStorm).mutate(undefined as never, {
      onSuccess: () => setServices(false),
    });
  }

  function StormCard({ row = false }: { row?: boolean }) {
    return (
      <article className={row ? "service-row" : "service-card"}>
        <span className="service-icon">
          <Icon name="storm" size={35} />
        </span>
        <div className="service-copy">
          <h3>Storm response</h3>
          <span className={`status-line ${t.stormArmed ? "is-green" : ""}`}>
            <span className="status-dot" />
            {t.stormArmed ? "Armed" : "Off"}
          </span>
          {row && <p className="muted">Outage-aware answering for a weather event.</p>}
        </div>
        <Button
          variant={t.stormArmed ? "default" : "primary"}
          disabled={!mayStorm || stormBusy}
          loading={stormBusy}
          onClick={toggleStorm}
        >
          {t.stormArmed ? "Turn off" : "Arm"}
        </Button>
      </article>
    );
  }

  function FlowCard({ flow, row = false }: { flow: FlowListItem; row?: boolean }) {
    const busy = runFlow.isPending && runFlow.variables === flow.id;
    return (
      <article className={row ? "service-row" : "service-card"}>
        <span className="service-icon">
          <Icon name={flow.pinIcon || "flow"} size={35} />
        </span>
        <div className="service-copy">
          <h3>{flow.pinLabel || flow.name}</h3>
          <span className={`status-line ${flow.armed ? "is-green" : ""}`}>
            <span className="status-dot" />
            {flow.armed ? "Armed" : flow.graphStale ? "Needs review" : "Ready"}
          </span>
          {row && <p className="muted">{flow.trigger}</p>}
        </div>
        <Button
          disabled={!mayArmFlow || flow.armed || flow.graphStale || runFlow.isPending}
          loading={busy}
          onClick={() => activateFlow(flow)}
        >
          {flow.armed ? "Armed" : "Activate"}
        </Button>
      </article>
    );
  }

  const CallActionsCard = (
    <article className="service-card">
      <span className="service-icon">
        <Icon name="message" size={35} />
      </span>
      <div className="service-copy">
        <h3>Call actions</h3>
        <span className="status-line">
          <span className="status-dot" />
          For a selected call
        </span>
      </div>
      <Link to="/calls" className="button">
        Open calls
      </Link>
    </article>
  );

  return (
    <>
      <section className="service-hero">
        <div className="hero-copy">
          <span className="eyebrow">Current service</span>
          <h1>{hero.title}</h1>
          <div className="hero-meta">
            <Badge tone={hero.tone}>
              {hero.tone === "green" && <Icon name="check" size={17} />}
              {hero.state}
            </Badge>
            <p>{hero.blurb}</p>
          </div>
          <Link className="button" to="/policies">
            <Icon name="sliders" />
            Service settings
          </Link>
        </div>
      </section>

      {t.stormArmed && (
        <div className="armed-strip">
          <Icon name="shield" />
          <strong>Storm response armed</strong>
          {demo && <Badge>Demo</Badge>}
          <button className="text-button" onClick={toggleStorm} disabled={!mayStorm || stormBusy}>
            Turn off
          </button>
        </div>
      )}

      <Panel
        title="Quick activate"
        action={
          <button className="text-button" onClick={() => setServices(true)}>
            All services <Icon name="arrow" size={18} />
          </button>
        }
        className="activation-panel"
      >
        <div className="service-shelf">
          <StormCard />
          {postureFlows.slice(0, 1).map((flow) => (
            <FlowCard key={flow.id} flow={flow} />
          ))}
          {CallActionsCard}
        </div>
      </Panel>

      <div className="overview-bottom">
        <section className="panel announcement">
          {outage ? (
            <>
              <AnnouncementPlayer text={outage.bannerText} />
              <div>
                <h3>{outage.label} announcement</h3>
                <p>“{outage.bannerText}”</p>
                {outage.announcementContradictsEta ? (
                  <small className="warn-text">
                    An ETA is set but the announcement still says none — reconcile on the outage.
                  </small>
                ) : (
                  <small className="muted">What affected callers hear</small>
                )}
              </div>
              <Link to="/outage" className="text-button">
                Edit wording
              </Link>
            </>
          ) : (
            <div className="empty-inline">
              <Icon name="check" size={24} />
              <div>
                <h3>No active outage</h3>
                <p className="muted">Announcements appear here when one is declared.</p>
              </div>
              <Link to="/outage" className="text-button">
                Outage board
              </Link>
            </div>
          )}
        </section>

        <Link to="/escalations" className={`panel attention ${notifying ? "urgent" : ""}`}>
          <span className="attention-icon">
            <Icon name="bell" size={27} />
          </span>
          <div>
            <h3>
              {openEscalations.length} {openEscalations.length === 1 ? "escalation" : "escalations"}
            </h3>
            <p>
              {notifying
                ? "Waiting for acknowledgment"
                : openEscalations.length
                  ? "Acknowledged · review details"
                  : "Nothing needs attention"}
            </p>
          </div>
          <span>
            Review <Icon name="arrow" size={18} />
          </span>
        </Link>
      </div>

      <div className="utility-strip">
        <Link to="/policies">
          <Icon name="user" />
          On-call &amp; routing
        </Link>
        <Link to="/outage">
          <Icon name="users" />
          {outage ? "1 active incident" : "No active incidents"}
        </Link>
        <Link to="/outage">
          <Icon name="info" />
          {outage?.etaText ? `ETA · ${outage.etaText}` : "ETA · operator entry only"}
        </Link>
      </div>

      <div className="night-summary">
        <div className="section-line">
          <h2>Tonight, so far</h2>
          <Link to="/reports" className="text-button">
            View reports <Icon name="arrow" size={16} />
          </Link>
        </div>
        <div className="stat-strip">
          <Stat value={t.stats.callsTonight} label="Calls handled" to="/calls" />
          <Stat value={t.stats.deflected} label="Deflected" to="/calls" />
          <Stat value={t.stats.proposalsPending} label="Awaiting approval" to="/proposals" />
          <Stat value={t.stats.escalationsUnresolved} label="Escalations" to="/escalations" />
        </div>
      </div>

      <Modal
        open={services}
        onClose={() => setServices(false)}
        title="Saved services"
        description="Activate a saved service without leaving the overview."
        sheet
      >
        <div className="stack">
          <StormCard row />
          {postureFlows.map((flow) => (
            <FlowCard key={flow.id} flow={flow} row />
          ))}
          <Link className="list-row" to="/calls" onClick={() => setServices(false)}>
            <Icon name="message" />
            <span>
              <strong>Call actions</strong>
              <small>Open a call to run an action</small>
            </span>
            <Icon name="chevron" />
          </Link>
          <Link className="text-button" to="/flows" onClick={() => setServices(false)}>
            Manage flows <Icon name="arrow" />
          </Link>
        </div>
        {demo && <Notice>Demo mode · outbound actions are simulated.</Notice>}
      </Modal>
    </>
  );
}
